import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight } from 'lucide-react'
import { api } from '@/lib/api/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { TableSkeleton } from '@/components/common/skeleton'
import { ErrorState } from '@/components/common/error-state'
import { EmptyState } from '@/components/common/empty-state'

export function BrandsPage() {
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null)

  const brandsQuery = useQuery({
    queryKey: ['brands'],
    queryFn: () => api.getBrands(),
  })

  const configQuery = useQuery({
    queryKey: ['brand-config', selectedBrand],
    queryFn: () => api.getBrandConfig(selectedBrand!),
    enabled: !!selectedBrand,
  })

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Brands</h2>
        <p className="text-sm text-muted-foreground">
          Brand configuration and channel settings
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Brand list</CardTitle>
          </CardHeader>
          <CardContent>
            {brandsQuery.isLoading ? (
              <TableSkeleton rows={4} />
            ) : brandsQuery.isError ? (
              <ErrorState
                message={brandsQuery.error.message}
                onRetry={() => brandsQuery.refetch()}
              />
            ) : !brandsQuery.data || brandsQuery.data.brands.length === 0 ? (
              <EmptyState message="No brands available for your account." />
            ) : (
              <ul className="divide-y">
                {brandsQuery.data.brands.map((brand) => (
                  <li key={brand.brand_id}>
                    <button
                      type="button"
                      onClick={() => setSelectedBrand(brand.brand_id)}
                      className={`flex w-full items-center justify-between px-2 py-3 text-left text-sm transition-colors hover:bg-muted/50 ${
                        selectedBrand === brand.brand_id ? 'bg-muted' : ''
                      }`}
                    >
                      <span className="font-mono font-medium">{brand.brand_id}</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              {selectedBrand ? `Config: ${selectedBrand}` : 'Brand config'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedBrand ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Select a brand to view its configuration.
              </p>
            ) : configQuery.isLoading ? (
              <TableSkeleton rows={6} />
            ) : configQuery.isError ? (
              <ErrorState
                message={configQuery.error.message}
                onRetry={() => configQuery.refetch()}
              />
            ) : configQuery.data ? (
              <BrandConfigTree config={configQuery.data} />
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function BrandConfigTree({ config }: { config: Record<string, unknown> }) {
  const enabledEvents = config.enabled_events as string[] | undefined
  const channels = config.channels as Record<string, { enabled?: boolean }> | undefined
  const currency = config.currency as string | undefined
  const defaultUrl = config.default_event_source_url as string | undefined

  return (
    <div className="space-y-6">
      {currency && (
        <ConfigSection title="Currency">
          <p className="font-mono">{currency}</p>
        </ConfigSection>
      )}

      {defaultUrl && (
        <ConfigSection title="Default event source URL">
          <p className="break-all font-mono text-xs">{defaultUrl}</p>
        </ConfigSection>
      )}

      {enabledEvents && (
        <ConfigSection title="Enabled events">
          <div className="flex flex-wrap gap-2">
            {enabledEvents.map((e) => (
              <Badge key={e} variant="outline">
                {e}
              </Badge>
            ))}
          </div>
        </ConfigSection>
      )}

      {channels && (
        <ConfigSection title="Channels">
          <ul className="space-y-2">
            {Object.entries(channels).map(([name, cfg]) => (
              <li
                key={name}
                className="flex items-center justify-between rounded-md border px-3 py-2"
              >
                <span className="font-medium capitalize">{name}</span>
                <Badge variant={cfg.enabled ? 'success' : 'secondary'}>
                  {cfg.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </li>
            ))}
          </ul>
        </ConfigSection>
      )}

      <ConfigSection title="Raw config">
        <pre className="max-h-64 overflow-auto rounded-md bg-muted p-4 text-xs">
          {JSON.stringify(config, null, 2)}
        </pre>
      </ConfigSection>
    </div>
  )
}

function ConfigSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="mb-2 text-sm font-medium text-muted-foreground">{title}</h4>
      {children}
    </div>
  )
}
